import { User } from '../../user';
import { isString, isArray, pick, map, filter, uniq, reduce, last, every, find } from 'lodash';
import { SLMovement, SLMovementLineOwnership, SLMovementLineOperation, SLMovementLineShareType, slMovementLineShareTypes, slMovementLineOwnerships, Share, SLDeletedMovement } from './movement';
import JSBI from 'jsbi';
import { add, subtract, lessThanZero, lessThanEZero } from './utils';

export type SLEntityType = 'COMPANY' | 'PERSON';

export class SLAddress {
  line1: string
  line2?: string
  line3?: string
  city?: string
  zip?: string
  country: string

  constructor(data: any = {}) {
    Object.assign(this, data);
  }

  get valid():boolean {
    // line1
    if(!isString(this.line1)) {
      return false;
    }
    
    let trimmedLine1 = this.line1.trim();
    
    if(trimmedLine1.length > 200) {
      return false;
    }
    
    if(trimmedLine1.length === 0) {
      return false;
    }

    // country
    if(!isString(this.country)) {
      return false;
    }

    let trimmedCountry = this.country.trim();

    if(trimmedCountry.length > 200) {
      return false;
    }

    if(trimmedCountry.length === 0) {
      return false;
    }

    return true;
  }
}

export interface SLEntityOwnedShareDetail {
  shareType: SLMovementLineShareType
  ownership: SLMovementLineOwnership
  amount: JSBI
}

export interface SLEntityOwnedShareItem {
  id: string
  name: string
  amount: JSBI
  percent: number
  details: SLEntityOwnedShareDetail[]
}

export interface SLEntityShareholders {
  total: JSBI
  details: SLEntityOwnedShareDetail[]
  items: SLEntityOwnedShareItem[]
}

export class SLEntityField {
  name: string
  value: string

  constructor(data: any = {}) {
    Object.assign(this, data);
  }

  get valid():boolean {
    // name
    if(!isString(this.name)) {
      return false;
    }

    let trimmedName = this.name.trim();
    
    if(trimmedName.length > 200) {
      return false;
    }
    
    if(trimmedName.length === 0) {
      return false;
    }

    // value
    if(!isString(this.value)) {
      return false;
    }

    return true;
  }
}

export class SLEntity {
  id: string
  version: number
  name: string
  rcs: string
  email: string
  phone: string
  address: SLAddress
  type: SLEntityType
  extraFields: SLEntityField[]
  movements: SLMovement[]
  hasMovements: boolean
  ownedShares: SLEntityOwnedShareItem[]
  totalShares: Share
  deletedMovements: SLDeletedMovement[]
  draftMovements: SLMovement[]
  deleted: boolean
  createdAt: number
  createdBy: User
  updatedAt: number
  updatedBy: User

  constructor(data: any = {}) {
    Object.assign(this, data);

    if(this.extraFields) {
      this.extraFields = map(this.extraFields, f => new SLEntityField(f));
    }

    if (this.address) {
      this.address = new SLAddress(this.address);
    }

    if(this.movements) {
      this.movements = map(this.movements, m => new SLMovement(m));
    }

    if(this.deletedMovements) {
      this.deletedMovements = map(this.deletedMovements, m => new SLDeletedMovement(m));
    }

    if(this.draftMovements) {
      this.draftMovements = map(this.draftMovements, m => new SLMovement(m));
    }

    if(this.createdBy) {
      this.createdBy = new User(this.createdBy);
    }

    if(this.updatedBy) {
      this.updatedBy = new User(this.updatedBy);
    }
  }

  get valid():boolean {

    // name
    if(!isString(this.name)) {
      return false;
    }

    let trimmedName = this.name.trim();

    if(trimmedName.length > 200) {
      return false;
    }

    if(trimmedName.length === 0) {
      return false;
    }

    // type
    if (!this.type) {
      return false;
    }

    // extraFields
    if (!isArray(this.extraFields)) {
      return false;
    }

    if (!every(this.extraFields, f => f.valid)) {
      return false;
    }

    // address
    if (!this.address || !this.address.valid) {
      return false;
    }

    // shareholders
    if (this.movements.length > 0) {

      const { total, details, items } = this.shareholders;
      
      if (lessThanEZero(total)) {
        return false;
      }
      
      for (let d of details) {
        if (lessThanZero(d.amount) || (d as SLEntityOwnedShareDetailWithValidation).invalid === true) {
          return false;
        }
      }
      
      for (let i of items) {
        if (lessThanZero(i.amount)) {
          return false;
        }
        
        for (let d of i.details) {
          if (lessThanZero(d.amount)) {
            return false;
          }
        }
      }
    
    }

    return true;
  }

  get shareholders(): SLEntityShareholders {
    interface SharesOp {
      id: string
      name: string
      amount: JSBI
      operation: SLMovementLineOperation;
      ownership: SLMovementLineOwnership;
      shareType: SLMovementLineShareType;
    }

    const shares: SharesOp[] = [];
    
    for(let m of this.movements) {
      for(let l of m.lines) {
        if (!l.entity || !l.amount) {
          continue;
        }

        shares.push({
          id: l.entity.id,
          name: l.entity.name,
          amount: l.amount,
          ownership: l.ownership,
          operation: l.operation,
          shareType: l.shareType,
        });
      }
    }

    const ids = uniq(map(shares, s => s.id));

    function sum(lines: SharesOp[]) {
      return reduce(
        lines,
        (prev, curr) => {
          if (curr.operation === 'CREDIT') {
            return add(prev, curr.amount);
          } else {
            return subtract(prev, curr.amount);
          }
        },
        JSBI.BigInt('0'),
      );
    }

    function buildDetails(shares: SharesOp[]) {
      const details: SLEntityOwnedShareDetailWithValidation[] = [];
  
      for (let shareType of slMovementLineShareTypes()) {
        for (let ownership of slMovementLineOwnerships()) {
          details.push({
            shareType,
            ownership,
            amount: sum(filter(shares, s => s.shareType === shareType && s.ownership === ownership)),
          });
        }
      }

      return details;
    }


    function calculateTotal(shares: SharesOp[]) {
      const PP = sum(filter(shares, s => s.ownership === 'PP'));
      // const UF = sum(filter(shares, s => s.ownership === 'UF'));
      const NP = sum(filter(shares, s => s.ownership === 'NP'));
      const AT = sum(filter(shares, s => s.ownership === 'AT'));

      return add(add(PP, AT), NP);
    }

    function validateUFNP(details: SLEntityOwnedShareDetailWithValidation[]) {
      const UF_ORD = find(details, s => s.ownership === 'UF' && s.shareType === 'ORD');
      const NP_ORD = find(details, s => s.ownership === 'NP' && s.shareType === 'ORD');
      
      const UF_PREF = find(details, s => s.ownership === 'UF' && s.shareType === 'PREF');
      const NP_PREF = find(details, s => s.ownership === 'NP' && s.shareType === 'PREF');
      
      if (!JSBI.equal(UF_ORD.amount, NP_ORD.amount)) {
        UF_ORD.invalid = true;
        NP_ORD.invalid = true;
      }
    
      if (!JSBI.equal(UF_PREF.amount, NP_PREF.amount)) {
        UF_PREF.invalid = true;
        NP_PREF.invalid = true;
      }
    }    

    const result = {
      total: calculateTotal(shares),

      details: buildDetails(shares),
      
      items: map(ids, id => {
        const lines = filter(shares, s => s.id === id);

        return {
          id,
          name: last(lines).name,
          amount: calculateTotal(lines),
          percent: 0, // TODO: review if having this whole function is needed!
          details: buildDetails(lines),
        };
      }),
    };

    // Mark invalid UF/NP
    validateUFNP(result.details);

    return result;
  }

  toCreateSLEntityInput() {
    return {
      name: this.name,
      type: this.type,
      rcs: this.rcs,
      email: this.email,
      phone: this.phone,
      extraFields: map(this.extraFields, f => pick(f, ['name', 'value'])),
      address: pick(this.address, ['line1', 'line2', 'line3', 'city', 'state', 'zip', 'country']),
    };
  }
  
  toUpdateSLEntityInput() {
    return {
      version: this.version,
      name: this.name,
      rcs: this.rcs,
      email: this.email,
      phone: this.phone,
      extraFields: map(this.extraFields, f => pick(f, ['name', 'value'])),
      address: pick(this.address, ['line1', 'line2', 'line3', 'city', 'state', 'zip', 'country']),
    };
  }
}

export interface SLBlockAddress {
  strandId: string
  sequenceNo: number
}

export class SLEntityVersion extends SLEntity {

  movementsLedger: any
  
  blockAddress: SLBlockAddress
  
  constructor(data: any = {}) {
    super(data);
  }
}

interface SLEntityOwnedShareDetailWithValidation extends SLEntityOwnedShareDetail {
  invalid?: boolean
}
